import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { MapPin, Loader2, Coins, Plus, X, Building2 } from 'lucide-react';
import { CityCard } from './CityCard';

const MAX_CITIES = 4;

export const CityInsights = ({ careerTitle, major }) => {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [customCity, setCustomCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    api.get('/career-simulator/us-cities').then(r => setCities(r.data.cities)).catch(() => {});
  }, []);

  const toggle = (name) => {
    setSelected(prev => prev.includes(name)
      ? prev.filter(c => c !== name)
      : prev.length >= MAX_CITIES ? prev : [...prev, name]);
  };

  const addCustom = () => {
    const name = customCity.trim();
    if (!name || selected.includes(name) || selected.length >= MAX_CITIES) return;
    setSelected([...selected, name]);
    setCustomCity('');
  };

  const compare = async () => {
    if (selected.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post('/career-simulator/city-insights', {
        career_title: careerTitle, major, cities: selected,
      });
      setInsights(res.data.insights);
    } catch (error) {
      if (error.response?.status === 402) {
        toast({ title: 'Credits Required', description: 'You need 1 credit for city insights', variant: 'default' });
        navigate('/pricing');
      } else {
        toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to load city insights', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const bestTags = insights ? {
    [insights.best_for_salary]: 'Top Salary',
    [insights.best_for_value]: 'Best Value',
    [insights.best_for_visa]: 'Visa Friendly',
  } : {};

  return (
    <Card data-testid="city-insights-card" className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20 md:ml-16">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 flex-wrap">
          <MapPin className="h-5 w-5 text-pink-400" />
          Where Should You Work? — US City Insights
          <Badge className="bg-amber-500/30 text-amber-200 border-amber-400/30 ml-auto">
            <Coins className="h-3 w-3 mr-1" /> 1 credit
          </Badge>
        </CardTitle>
        <p className="text-sm text-white/60">Pick up to {MAX_CITIES} cities to compare salaries, rent, hiring demand and H-1B odds for a {careerTitle}.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2">
          {cities.map(c => {
            const active = selected.includes(c.name);
            return (
              <button
                key={c.name}
                data-testid={`city-chip-${c.name.split(',')[0].toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => toggle(c.name)}
                disabled={!active && selected.length >= MAX_CITIES}
                className={`px-3 py-2 rounded-full text-sm transition-all border ${
                  active
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white border-transparent shadow-lg shadow-pink-500/30'
                    : 'bg-white/5 text-white/80 border-white/15 hover:bg-white/15 disabled:opacity-40'
                }`}
              >
                {c.name}
                <span className="text-xs opacity-70 ml-1">· {c.tag}</span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2">
          <Input
            data-testid="city-custom-input"
            placeholder="Add another US city (e.g., Phoenix, AZ)"
            value={customCity}
            onChange={e => setCustomCity(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustom()}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
          />
          <Button data-testid="city-add-custom-btn" onClick={addCustom} variant="outline" className="border-white/30 text-white hover:bg-white/10" disabled={selected.length >= MAX_CITIES}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="city-selected-list">
            <span className="text-xs text-white/60">Comparing:</span>
            {selected.map(c => (
              <Badge key={c} className="bg-white/15 text-white gap-1">
                {c}
                <button onClick={() => toggle(c)} aria-label={`Remove ${c}`}><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        )}

        <Button
          data-testid="city-compare-btn"
          onClick={compare}
          disabled={loading || selected.length === 0}
          className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Building2 className="h-4 w-4 mr-2" />}
          {loading ? 'Crunching city data...' : `Compare ${selected.length || ''} ${selected.length === 1 ? 'City' : 'Cities'}`}
        </Button>

        {insights?.cities?.length > 0 && (
          <div className="space-y-4 pt-2" data-testid="city-insights-results">
            <div className={`grid gap-4 ${insights.cities.length > 1 ? 'md:grid-cols-2' : ''}`}>
              {insights.cities.map((c, i) => <CityCard key={i} city={c} tag={bestTags[c.city]} />)}
            </div>
            {insights.recommendation && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-white/15">
                <p className="text-xs uppercase tracking-wide text-pink-300 mb-1">Ashish's Take</p>
                <p className="text-white/90 text-sm" data-testid="city-recommendation">{insights.recommendation}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
